import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiClock, FiStar, FiAnchor, FiMap, FiAward, FiSearch, FiCalendar, FiHelpCircle } = FiIcons;

function MultiplicationTreasureHuntGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();

  const [gameMode, setGameMode] = useState('menu');
  const [currentTable, setCurrentTable] = useState('mixed');
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [coins, setCoins] = useState(0);
  
  const [question, setQuestion] = useState(null);
  const [cards, setCards] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [maxTime, setMaxTime] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [bossProgress, setBossProgress] = useState(0);
  const [showHint, setShowHint] = useState(false);
  
  const [tableOfDay] = useState(() => Math.floor(Math.random() * 10) + 1);

  const theme = {
    bg: "bg-gradient-to-b from-blue-400 to-blue-600",
    cardBg: "bg-amber-100",
    cardBorder: "border-amber-700",
    text: "text-amber-900"
  };

  const generateRound = (mode) => {
    let num1, num2;
    const isBoss = mode === 'boss';
    const isReverse = mode === 'reverse';
    
    if (currentTable !== 'mixed' && !isBoss) {
      num1 = currentTable;
      num2 = Math.floor(Math.random() * 10) + 1;
    } else {
      num1 = Math.floor(Math.random() * 10) + 1;
      num2 = Math.floor(Math.random() * 10) + 1;
    }

    const product = num1 * num2;
    const factString = `${num1} × ${num2}`;
    
    let timeLimit = (num1 <= 5 && num2 <= 5) ? 6 : 12;
    if (isReverse) timeLimit += 3;

    const newCards = [];
    const usedValues = new Set();
    
    if (isReverse) {
      // REVERSE MODE: Enhanced "Find the Parents" logic
      newCards.push({ value: factString, isCorrect: true });
      usedValues.add(factString);
      
      // Generate smarter distractors for educational value
      while (newCards.length < 12) {
        let distractorFact;
        const random = Math.random();
        
        if (random < 0.3 && newCards.length < 8) {
          // 30% chance: Create a fact with the SAME PRODUCT (tricky!)
          // This teaches that multiple facts can have the same answer
          const factors = getFactorsOfProduct(product);
          if (factors.length > 1) {
            const altFactor = factors.find(f => f !== `${num1},${num2}` && f !== `${num2},${num1}`);
            if (altFactor) {
              const [a, b] = altFactor.split(',');
              distractorFact = `${a} × ${b}`;
            }
          }
        }
        
        if (!distractorFact) {
          // Generate random fact with CLOSE product (off by 1-5)
          const offset = Math.floor(Math.random() * 5) + 1;
          const sign = Math.random() > 0.5 ? 1 : -1;
          const targetProduct = Math.max(1, product + (offset * sign));
          
          const n1 = Math.floor(Math.random() * 10) + 1;
          const n2 = Math.floor(targetProduct / n1);
          
          if (n2 >= 1 && n2 <= 10) {
            distractorFact = `${n1} × ${n2}`;
          } else {
            distractorFact = `${Math.floor(Math.random() * 10) + 1} × ${Math.floor(Math.random() * 10) + 1}`;
          }
        }
        
        if (!usedValues.has(distractorFact) && distractorFact !== factString) {
          newCards.push({ value: distractorFact, isCorrect: false });
          usedValues.add(distractorFact);
        }
      }
    } else {
      // STANDARD MODE: Find the Product
      newCards.push({ value: product, isCorrect: true });
      usedValues.add(product);

      while (newCards.length < 12) {
        const n1 = Math.floor(Math.random() * 10) + 1;
        const n2 = Math.floor(Math.random() * 10) + 1;
        const p = n1 * n2;
        if (!usedValues.has(p)) {
          newCards.push({ value: p, isCorrect: false });
          usedValues.add(p);
        }
      }
    }

    const shuffledCards = newCards.sort(() => Math.random() - 0.5);

    setQuestion({ 
      display: isReverse ? product : factString, 
      correctValue: isReverse ? factString : product,
      num1,
      num2,
      product
    });
    setCards(shuffledCards);
    setTimeLeft(timeLimit);
    setMaxTime(timeLimit);
    setFeedback(null);
    setShowHint(false);
  };

  // Helper function to get all factor pairs of a number
  const getFactorsOfProduct = (num) => {
    const factors = [];
    for (let i = 1; i <= Math.min(num, 10); i++) {
      if (num % i === 0) {
        const pair = num / i;
        if (pair <= 10 && pair >= 1) {
          factors.push(`${i},${pair}`);
        }
      }
    }
    return factors;
  };

  useEffect(() => {
    if ((gameMode === 'playing' || gameMode === 'boss' || gameMode === 'reverse') && timeLeft > 0 && !feedback) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 0.1) {
            handleTimeout();
            return 0;
          }
          return prev - 0.1;
        });
      }, 100);
      return () => clearInterval(timer);
    }
  }, [gameMode, timeLeft, feedback]);

  const handleTimeout = () => {
    setFeedback('timeout');
    setStreak(0);
    dispatch({ type: 'ADD_WRONG_ANSWER' });
    
    if (gameMode === 'boss') {
      setTimeout(() => setGameMode('result'), 1500);
    } else {
      setTimeout(() => generateRound(gameMode), 1500);
    }
  };

  const handleCardClick = (card) => {
    if (feedback) return;

    if (card.isCorrect) {
      const points = Math.ceil(timeLeft) * 10;
      const isTableOfDay = currentTable === tableOfDay;
      const finalPoints = isTableOfDay ? points * 2 : points;

      setScore(prev => prev + finalPoints);
      setStreak(prev => prev + 1);
      setCoins(prev => prev + (isTableOfDay ? 2 : 1));
      setFeedback('correct');
      dispatch({ type: 'ADD_SCORE', payload: finalPoints });

      if (gameMode === 'boss') {
        const newProgress = bossProgress + 1;
        setBossProgress(newProgress);
        if (newProgress >= 5) {
          setTimeout(() => {
            dispatch({ type: 'GAME_COMPLETED', payload: { questId: 'treasure-hunt', bonusTokens: 50 } });
            setGameMode('result');
          }, 1000);
          return;
        }
      }

      setTimeout(() => generateRound(gameMode), 1000);
    } else {
      setFeedback({ status: 'wrong', clicked: card.value });
      setStreak(0);
      dispatch({ type: 'ADD_WRONG_ANSWER' });
      
      if (gameMode === 'boss') {
        setTimeout(() => setGameMode('result'), 1500);
      } else {
        setTimeout(() => setFeedback(null), 1000);
      }
    }
  };

  const handleHintClick = () => {
    setShowHint(!showHint);
    // Small time penalty for using hint
    setTimeLeft(prev => Math.max(1, prev - 2));
  };

  const startGame = (mode, table = 'mixed') => {
    setGameMode(mode);
    setCurrentTable(table);
    setScore(0);
    setStreak(0);
    setCoins(0);
    setBossProgress(0);
    generateRound(mode);
  };

  const renderMenu = () => (
    <div className="max-w-4xl mx-auto p-4">
      <div className="bg-amber-50 rounded-3xl p-8 shadow-2xl border-4 border-amber-600 text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 text-6xl opacity-20 transform -rotate-12">🏴‍☠️</div>
        <div className="absolute bottom-0 right-0 text-6xl opacity-20 transform rotate-12">💎</div>

        <h1 className="text-4xl font-kid text-amber-800 mb-2">Multiplication Treasure Hunt</h1>
        <p className="font-comic text-amber-700 text-lg mb-6">
          Race against time to find the treasure numbers!
        </p>

        <div className="bg-yellow-100 border-2 border-yellow-400 rounded-xl p-3 mb-6 inline-flex items-center gap-3 animate-pulse">
          <SafeIcon icon={FiCalendar} className="text-yellow-600 text-xl" />
          <span className="font-comic text-amber-800">
            <strong>Table of the Day:</strong> {tableOfDay}× (2x Coins!)
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Classic Mode */}
          <div className="bg-white rounded-2xl p-6 border-2 border-blue-300 shadow-lg">
            <div className="relative z-10">
              <h2 className="text-2xl font-kid text-blue-600 mb-2 flex items-center justify-center gap-2">
                <SafeIcon icon={FiMap} /> Find the Product
              </h2>
              <p className="font-comic text-sm text-gray-500 mb-4">See 3×4, find 12!</p>
              
              <div className="grid grid-cols-5 gap-2 mb-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <button
                    key={num}
                    onClick={() => startGame('playing', num)}
                    className={`font-kid py-2 rounded-lg border transition-colors ${
                      num === tableOfDay 
                        ? 'bg-yellow-100 border-yellow-400 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100'
                    }`}
                  >
                    {num}{num === tableOfDay ? '★' : ''}
                  </button>
                ))}
              </div>
              <button
                onClick={() => startGame('playing', 'mixed')}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-kid py-2 rounded-xl shadow-md"
              >
                Mixed Tables
              </button>
            </div>
          </div>

          {/* Reverse Mode */}
          <div className="bg-white rounded-2xl p-6 border-2 border-purple-300 shadow-lg">
            <h2 className="text-2xl font-kid text-purple-600 mb-2 flex items-center justify-center gap-2">
              <SafeIcon icon={FiSearch} /> Find the Parents
            </h2>
            <p className="font-comic text-sm text-gray-500 mb-2">See 12, find 3×4!</p>
            <p className="font-comic text-xs text-purple-600 mb-4">
              💡 Master memorization by finding the factors!
            </p>
            <div className="text-4xl mb-3">🔍</div>
            <button
              onClick={() => startGame('reverse', 'mixed')}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white font-kid py-3 rounded-xl shadow-lg hover:scale-105 transition-transform"
            >
              Start Detective Mode
            </button>
          </div>

          {/* Boss Mode */}
          <div className="md:col-span-2 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 border-2 border-red-300 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <h2 className="text-2xl font-kid text-red-600 mb-1 flex items-center gap-2">
                  <SafeIcon icon={FiAnchor} /> Boss Battle
                </h2>
                <p className="font-comic text-gray-600">
                  Get 5 correct in a row to defeat the Pirate Captain!
                </p>
              </div>
              <button
                onClick={() => startGame('boss', 'mixed')}
                className="bg-red-500 hover:bg-red-600 text-white font-kid px-8 py-3 rounded-xl shadow-lg hover:scale-105 transition-transform flex items-center gap-2"
              >
                <span>⚔️</span> Battle!
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGame = () => (
    <div className="max-w-4xl mx-auto p-4">
      {/* HUD */}
      <div className="flex justify-between items-center mb-6 bg-black/30 backdrop-blur-md rounded-2xl p-4 text-white border-2 border-amber-400">
        <div className="flex items-center gap-2">
          <SafeIcon icon={gameMode === 'boss' ? FiAward : FiStar} className="text-yellow-400 text-2xl" />
          <span className="font-kid text-xl">
            {gameMode === 'boss' ? `Boss: ${bossProgress}/5` : `Coins: ${coins}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiClock} className="text-2xl" />
          <div className="w-32 h-4 bg-gray-700 rounded-full overflow-hidden border border-gray-500">
            <motion.div 
              className={`h-full ${timeLeft < 2 ? 'bg-red-500' : 'bg-green-500'}`}
              initial={{ width: '100%' }}
              animate={{ width: `${(timeLeft / maxTime) * 100}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
        </div>
      </div>

      {/* Question Area */}
      <div className="text-center mb-8">
        <motion.div 
          key={question.display}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="inline-block bg-amber-100 border-4 border-amber-700 rounded-3xl px-12 py-6 shadow-2xl transform rotate-1 relative"
        >
          <div className="text-6xl font-kid text-amber-900 drop-shadow-sm">
            {question.display}
          </div>
          <div className="text-amber-700 font-comic mt-2 text-sm uppercase tracking-wider">
            {gameMode === 'reverse' ? 'Find the Fact!' : 'Find the Product!'}
          </div>
          
          {/* Hint Button for Reverse Mode */}
          {gameMode === 'reverse' && (
            <button
              onClick={handleHintClick}
              className="mt-3 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-xl font-comic text-sm flex items-center gap-2 mx-auto"
            >
              <SafeIcon icon={FiHelpCircle} />
              {showHint ? 'Hide Hint' : 'Show Hint (-2s)'}
            </button>
          )}
          
          {/* Hint Display */}
          <AnimatePresence>
            {showHint && gameMode === 'reverse' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 bg-purple-100 border-2 border-purple-300 rounded-xl p-3"
              >
                <p className="text-purple-800 font-comic text-sm">
                  💡 <strong>Hint:</strong> Think of two numbers that multiply to {question.product}
                </p>
                <p className="text-purple-600 font-comic text-xs mt-1">
                  Try factors like: {getFactorsOfProduct(question.product).slice(0, 3).map(f => {
                    const [a, b] = f.split(',');
                    return `${a}×${b}`;
                  }).join(', ')}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
        {cards.map((card, idx) => (
          <motion.button
            key={`${idx}-${card.value}`}
            layout
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleCardClick(card)}
            disabled={!!feedback}
            className={`
              relative h-24 rounded-xl text-2xl md:text-3xl font-kid shadow-lg border-b-4 transition-colors
              ${feedback === 'correct' && card.isCorrect 
                ? 'bg-green-400 border-green-600 text-white' 
                : feedback?.status === 'wrong' && !card.isCorrect && feedback.clicked === card.value
                ? 'bg-red-400 border-red-600 text-white'
                : 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
              }
            `}
          >
            {card.value}
            {feedback === 'correct' && card.isCorrect && (
              <motion.div
                initial={{ scale: 0, y: 0 }}
                animate={{ scale: 1.5, y: -50, opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center text-yellow-400 z-10"
              >
                <SafeIcon icon={FiStar} />
              </motion.div>
            )}
          </motion.button>
        ))}
      </div>

      {/* Feedback Overlays */}
      <AnimatePresence>
        {feedback === 'correct' && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 pointer-events-none flex items-center justify-center z-50"
          >
            <div className="text-8xl drop-shadow-2xl">✨</div>
          </motion.div>
        )}
        {feedback?.status === 'wrong' && (
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: [0, -10, 10, -10, 10, 0] }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 pointer-events-none flex items-center justify-center z-50"
          >
            <div className="text-8xl drop-shadow-2xl">❌</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const renderResult = () => (
    <div className="max-w-md mx-auto p-4">
      <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-amber-500 text-center">
        <div className="text-6xl mb-4">
          {gameMode === 'boss' && bossProgress >= 5 ? '👑' : '🏴‍☠️'}
        </div>
        <h2 className="text-3xl font-kid text-amber-800 mb-2">
          {gameMode === 'boss' && bossProgress >= 5 ? 'Captain Defeated!' : 'Round Over!'}
        </h2>
        <p className="font-comic text-amber-700 mb-6">
          {gameMode === 'boss' && bossProgress >= 5 
            ? "You are the new Multiplication Master!" 
            : "Great treasure hunting!"}
        </p>

        <div className="bg-amber-50 rounded-xl p-4 mb-6">
          <div className="text-sm font-comic text-amber-600">Total Coins Earned</div>
          <div className="text-4xl font-kid text-amber-800">{coins}</div>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => setGameMode('menu')}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-kid py-3 rounded-xl shadow-lg"
          >
            Play Again
          </button>
          <button
            onClick={() => navigate('/quest-map')}
            className="w-full bg-white hover:bg-gray-50 text-amber-700 font-kid py-3 rounded-xl border-2 border-amber-200"
          >
            Back to Map
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${theme.bg} overflow-hidden font-sans`}>
      <div className="fixed inset-0 pointer-events-none opacity-10">
        <div className="absolute top-10 left-10 text-8xl">⚓</div>
        <div className="absolute bottom-20 right-20 text-8xl">🗺️</div>
        <div className="absolute top-1/2 left-1/2 text-9xl transform -translate-x-1/2 -translate-y-1/2">🌊</div>
      </div>

      <div className="relative z-10 pt-4">
        {gameMode !== 'menu' && (
          <button 
            onClick={() => setGameMode('menu')}
            className="absolute top-4 left-4 bg-white/20 hover:bg-white/30 text-white p-2 rounded-xl backdrop-blur-sm transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="text-2xl" />
          </button>
        )}

        {gameMode === 'menu' && renderMenu()}
        {(gameMode === 'playing' || gameMode === 'boss' || gameMode === 'reverse') && renderGame()}
        {gameMode === 'result' && renderResult()}
      </div>
    </div>
  );
}

export default MultiplicationTreasureHuntGame;